import React from 'react';
import Confetti from 'react-confetti';
import { css } from '@emotion/react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { theme } from 'twin.macro';
import { withWebinarPlayerContext } from '../context';
import { Portal } from '$/components/Portal';
import { PlayerMode } from '../types';

export const ScoreSpinner: React.FC = withWebinarPlayerContext(
  ['cardInteractions', 'mode', 'dimensions', 'ewebinar', 'transitions'],
  'ScoreSpinner'
)(({ context }) => {
  const { cardInteractions, mode, dimensions, ewebinar, transitions } = context;

  const { total, totalPoints } = React.useMemo(() => {
    let total = 0;
    let totalPoints = 0;

    for (const i of cardInteractions) {
      total += i.hasAnswers() ? i.points() : 0;
      totalPoints += i.isAnswered() ? i.points() : 0;
    }
    return { total, totalPoints };
  }, [cardInteractions]);

  const percent = Math.floor((totalPoints / total) * 100);
  const [display, setDisplay] = React.useState(percent);

  // Only called to update text after animation is done
  const updateScore = React.useCallback(() => {
    setDisplay(percent);
  }, [percent]);

  const [shootConfetti, setShootConfetti] = React.useState(false);
  const scoreDone = display >= 100;
  React.useEffect(() => {
    if (!scoreDone) return;

    setShootConfetti(true);
    window.setTimeout(() => setShootConfetti(false), 3000);
  }, [scoreDone]);

  const border = 9;
  const side = dimensions.actionsPane.idock.widthRem * 14;
  const r = side / 2 - border + 1;
  const c = 2 * Math.PI * r;

  if (mode === PlayerMode.edit) return null;

  return (
    <>
      <Portal containerId='confetti'>
        <div
          className='ScoreSpinner flex items-center justify-center z-player-score'
          css={css`
            position: absolute;
            top: ${-dimensions.actionsPane.marginTopPx + 1}px;
            right: 0;
            width: ${side}px;
            height: ${side}px;
          `}
        >
          <svg
            id='svg'
            width={side}
            height={side}
            viewBox={`0 0 ${side} ${side}`}
            version='1.1'
            xmlns='http://www.w3.org/2000/svg'
            css={css`
              circle {
                stroke-width: ${border}px;
                &#indicator {
                  transform: rotate(-90deg);
                  transform-origin: center;
                }
              }
            `}
          >
            <circle
              id='track'
              r={r}
              cx={side / 2}
              cy={side / 2}
              fill='#fff'
              stroke={ewebinar.primaryColor}
            />
            <motion.circle
              id='indicator'
              r={r}
              cx={side / 2}
              cy={side / 2}
              fill='transparent'
              stroke='#cccccc'
              strokeDasharray={c}
              strokeLinecap='square'
              transition={transitions['score.change']}
              initial={{ strokeDashoffset: 0 }}
              animate={{ strokeDashoffset: (percent / 100) * c }}
              onAnimationStart={(): void => {
                window.setTimeout(updateScore, (transitions['score.change'].delay ?? 0) * 1000);
              }}
            />
            <CountUp
              duration={transitions['score.change'].duration}
              preserveValue={true}
              delay={0}
              start={0}
              end={display}
              suffix='%'
            >
              {({ countUpRef }): JSX.Element => (
                <text
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ref={countUpRef as any}
                  x={side / 2}
                  y={side / 2}
                  textAnchor={'middle'}
                  alignmentBaseline={'central'}
                  dominantBaseline={'central'}
                  fill={theme`colors.gray.0`.toString()}
                  className='font-semibold font-mono'
                  css={css`
                    font-size: 13px;
                  `}
                />
              )}
            </CountUp>
          </svg>
        </div>
        <Confetti
          run={percent === 100}
          width={dimensions.webinarArea.width}
          height={dimensions.webinarArea.height}
          recycle={shootConfetti}
          initialVelocityX={10}
          initialVelocityY={12}
          confettiSource={{ x: dimensions.webinarArea.width - 40, y: 30, w: 10, h: 10 }}
          className='z-player-confetti!'
        />
      </Portal>
    </>
  );
});
